import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API, useAuth, useCart, useCurrency } from "../../App";
import UserLayout from "../../components/UserLayout";
import ItemCustomizationDialog from "./ItemCustomizationDialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { MapPin, Clock, ChevronRight, Search, ShoppingCart, Star, Plus, Minus, ArrowLeft, Store, AlertCircle, Navigation, Dumbbell } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function UserMenu() {
  const { user } = useAuth();
  const { symbol } = useCurrency();
  const { cart, addToCart, getItemCount, getCartQuantityForItem, getTotal, updateQuantity, clearCart } = useCart();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [step, setStep] = useState("address");
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [kitchens, setKitchens] = useState([]);
  const [selectedKitchen, setSelectedKitchen] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuLoading, setMenuLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [customizeItem, setCustomizeItem] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");

  const [pickupMode, setPickupMode] = useState(() => sessionStorage.getItem("sm_pickup_mode") === "true");
  const [allKitchens, setAllKitchens] = useState([]);

  const enablePickupMode = async () => {
    setPickupMode(true);
    sessionStorage.setItem("sm_pickup_mode", "true");
    setLoading(true);
    try {
      const res = await fetch(`${API}/user/all-kitchens`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setAllKitchens(data);
        setKitchens(data);
        setStep("kitchen");
      }
    } catch { toast.error("Failed to load kitchens"); }
    finally { setLoading(false); }
  };

  const exitPickupMode = () => {
    setPickupMode(false);
    sessionStorage.removeItem("sm_pickup_mode");
    setStep("address");
    setSearchParams({});
  };

  useEffect(() => { fetchAddresses(); }, []);

  // Restore pickup mode on mount
  useEffect(() => {
    if (sessionStorage.getItem("sm_pickup_mode") === "true" && step === "address") {
      // Re-enable pickup mode
      setPickupMode(true);
      setLoading(true);
      fetch(`${API}/user/all-kitchens`, { credentials: "include" })
        .then(res => res.ok ? res.json() : [])
        .then(data => { setAllKitchens(data); setKitchens(data); setStep("kitchen"); })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore address from session on load
  useEffect(() => {
    if (addresses.length === 0) return;
    const urlStep = searchParams.get("step");
    const urlKitchen = searchParams.get("kitchen");
    const urlAddress = searchParams.get("address");

    // Priority: URL params > sessionStorage
    const addrId = urlAddress || sessionStorage.getItem("sm_address_id");
    if (addrId) {
      const addr = addresses.find((a) => a.address_id === addrId);
      if (addr) {
        setSelectedAddress(addr);
        if (urlKitchen && urlStep === "menu") {
          setSelectedKitchen({ kitchen_id: urlKitchen });
        }
        // Auto-load kitchens if we have a stored address
        if (!urlStep || urlStep === "address") {
          handleAddressSelect(addr, true);
        } else if (urlStep === "kitchen") {
          handleAddressSelect(addr, true);
        } else if (urlStep === "menu" && urlKitchen) {
          // Load kitchens first, then trigger menu load
          handleAddressSelect(addr, true).then(() => {
            handleKitchenSelect({ kitchen_id: urlKitchen });
          });
        }
        return;
      }
    }
    if (urlStep) setStep(urlStep);
  }, [addresses]);

  const fetchAddresses = async () => {
    try {
      const res = await fetch(`${API}/addresses`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setAddresses(data);
        if (data.length === 0) setStep("address");
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleAddressSelect = async (addr, silent = false) => {
    setSelectedAddress(addr);
    sessionStorage.setItem("sm_address_id", addr.address_id);
    localStorage.setItem("supermorsel_checkout_address", addr.address_id);
    setLoading(true);
    try {
      const res = await fetch(`${API}/user/serviceable-kitchens?lat=${addr.lat || 0}&lng=${addr.lng || 0}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setKitchens(data);
        if (!silent) {
          setStep("kitchen");
          setSearchParams({ step: "kitchen", address: addr.address_id });
        } else {
          // On restore, go to kitchen step if no further navigation needed
          const urlStep = searchParams.get("step");
          if (!urlStep || urlStep === "address" || urlStep === "kitchen") {
            setStep("kitchen");
          }
        }
      }
    } catch { toast.error("Failed to find kitchens"); }
    finally { setLoading(false); }
  };

  const handleKitchenSelect = async (kit) => {
    if (!kit.is_open) { toast.error("This kitchen is currently closed"); return; }
    // If cart has items from a different kitchen, warn and clear
    if (cart.length > 0 && cart[0].kitchen_id && cart[0].kitchen_id !== kit.kitchen_id) {
      if (!window.confirm(`You have items from ${cart[0].kitchen_code || 'another kitchen'} in your cart. Switching to ${kit.kitchen_code || 'this kitchen'} will empty your cart. Continue?`)) return;
      clearCart();
    }
    setSelectedKitchen(kit);
    setMenuLoading(true);
    try {
      const res = await fetch(`${API}/user/kitchen-menu/${kit.kitchen_id}`, { credentials: "include" });
      if (res.ok) {
        setMenuItems(await res.json());
        setStep("menu");
        setSearchParams({ step: "menu", address: selectedAddress?.address_id, kitchen: kit.kitchen_id });
      }
    } catch { toast.error("Failed to load menu"); }
    finally { setMenuLoading(false); }
  };

  const handleAddToCart = (item) => {
    const hasVariations = item.variations?.length > 0;
    const hasAddons = item.addon_groups?.length > 0;
    if (hasVariations || hasAddons) {
      setCustomizeItem(item);
      return;
    }
    // Check if switching kitchen
    if (cart.length > 0 && cart[0].kitchen_id && cart[0].kitchen_id !== selectedKitchen?.kitchen_id) {
      if (!window.confirm(`Your cart has items from ${cart[0].kitchen_code || 'another kitchen'}. Adding from this kitchen will empty your cart. Continue?`)) return;
      clearCart();
    }
    addToCart({
      item_id: item.item_id,
      name: item.name,
      price: item.base_price || item.price || 0,
      image_url: item.image_path ? `${BACKEND_URL}/api/files/${item.image_path}` : item.image_url,
      selected_variations: [],
      selected_addons: [],
      kitchen_id: selectedKitchen?.kitchen_id,
      kitchen_code: selectedKitchen?.kitchen_code,
    });
    toast.success(`${item.name} added to cart`);
  };

  const handleCustomizationAdd = (cartItem) => {
    // Check kitchen switch for customized items too
    if (cart.length > 0 && cart[0].kitchen_id && cart[0].kitchen_id !== selectedKitchen?.kitchen_id) {
      if (!window.confirm(`Your cart has items from ${cart[0].kitchen_code || 'another kitchen'}. Adding from this kitchen will empty your cart. Continue?`)) return;
      clearCart();
    }
    addToCart({
      ...cartItem,
      kitchen_id: selectedKitchen?.kitchen_id,
      kitchen_code: selectedKitchen?.kitchen_code,
    });
    toast.success(`${cartItem.name} added to cart`);
  };

  const cartCount = getItemCount();
  const cartTotal = getTotal();

  const filteredMenu = menuItems.filter((item) => {
    const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase()) ||
      (item.category || "").toLowerCase().includes(search.toLowerCase());
    const matchesCategory = selectedCategory === "all" || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const allCategories = [...new Set(menuItems.map((i) => i.category).filter(Boolean))].sort();
  const categories = [...new Set(filteredMenu.map((i) => i.category).filter(Boolean))].sort();

  if (loading && step === "address") {
    return <UserLayout><div className="animate-pulse space-y-4 p-4">{[1, 2, 3].map((i) => <div key={i} className="h-20 bg-gray-100 rounded-xl" />)}</div></UserLayout>;
  }

  return (
    <UserLayout>
      <div className="pb-24" data-testid="user-menu-page">
        {/* Step: Address Selection */}
        {step === "address" && (
          <div className="animate-fade-in">
            <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
              <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-4">
                <MapPin size={28} className="text-green-600" />
              </div>
              <h1 className="text-xl font-bold text-gray-900">Select Delivery Address</h1>
              <p className="text-sm text-gray-500 mt-1 max-w-xs">Choose where you'd like your food delivered, and we'll show kitchens near you</p>
            </div>

            {addresses.length === 0 ? (
              <div className="text-center py-8 px-4">
                <p className="text-gray-600 font-medium mb-2">No delivery address found</p>
                <p className="text-sm text-gray-400 mb-4">Add a shipping address to browse nearby kitchens</p>
                <Button onClick={() => navigate("/profile?tab=addresses")} className="bg-green-600 hover:bg-green-700 text-white" data-testid="add-address-redirect-btn">
                  <MapPin size={16} className="mr-2" /> Add Address
                </Button>
              </div>
            ) : (
              <div className="px-4 space-y-3">
                {addresses.map((addr) => (
                  <button key={addr.address_id} onClick={() => handleAddressSelect(addr)}
                    className="w-full flex items-center gap-3 p-4 bg-white rounded-2xl border border-gray-100 hover:border-green-300 hover:shadow-sm transition-all text-left"
                    data-testid={`address-option-${addr.address_id}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
                      <MapPin size={18} className="text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-sm">{addr.label || "Address"}</p>
                      <p className="text-[10px] text-gray-400 truncate">{addr.address_line}, {addr.city}</p>
                      {addr.pincode && <p className="text-[10px] text-gray-500">{addr.state} - {addr.pincode}</p>}
                    </div>
                    <ChevronRight size={18} className="text-gray-400" />
                  </button>
                ))}
                <button onClick={() => navigate("/profile?tab=addresses")} className="w-full p-3 border-2 border-dashed border-gray-200 rounded-2xl text-sm text-gray-500 hover:border-green-300 hover:text-green-600 transition-colors" data-testid="add-new-address-btn">
                  <Plus size={16} className="inline mr-1" /> Add New Address
                </button>
                {!pickupMode && (
                  <button onClick={enablePickupMode} className="w-full p-3 bg-green-700 hover:bg-green-800 rounded-2xl text-sm font-semibold text-white transition-colors flex items-center justify-center gap-2" data-testid="pickup-mode-btn">
                    <Store size={16} /> Pickup Myself
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step: Kitchen Selection */}
        {step === "kitchen" && (
          <div className="animate-fade-in">
            <div className="flex flex-col items-center justify-center py-6 px-4 text-center">
              <button onClick={() => { if (pickupMode) { exitPickupMode(); } else { setStep("address"); setSearchParams({}); } }} className="self-start flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4" data-testid="back-to-address-btn">
                <ArrowLeft size={16} /> {pickupMode ? "Exit Pickup Mode" : "Change Address"}
              </button>
              <div className="w-14 h-14 rounded-full bg-orange-50 flex items-center justify-center mb-3">
                <Store size={24} className="text-orange-500" />
              </div>
              <h1 className="text-xl font-bold text-gray-900">{pickupMode ? "Select Kitchen for Pickup" : "Nearby Kitchens"}</h1>
              {!pickupMode && <p className="text-sm text-gray-500 mt-1">
                <MapPin size={12} className="inline" /> Delivering to: {selectedAddress?.address_line}, {selectedAddress?.city}
              </p>}
            </div>

            {loading ? (
              <div className="px-4 space-y-3">{[1, 2].map((i) => <div key={i} className="h-48 bg-gray-100 rounded-xl animate-pulse" />)}</div>
            ) : kitchens.length === 0 ? (
              <div className="text-center py-12 px-4">
                <Store className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 font-medium mb-2">No kitchens nearby</p>
                <p className="text-sm text-gray-400">No Super Morsel kitchen is within serviceable range of your address</p>
                <Button onClick={() => { setStep("address"); setSearchParams({}); }} variant="outline" className="mt-4">Try another address</Button>
              </div>
            ) : (
              <div className="px-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {kitchens.map((kit) => (
                  <button key={kit.kitchen_id} onClick={() => handleKitchenSelect(kit)}
                    className={`w-full rounded-2xl border overflow-hidden transition-all text-left ${kit.is_open ? "bg-white border-gray-100 hover:border-green-300 hover:shadow-md" : "bg-gray-50 border-gray-100 opacity-70"}`}
                    disabled={!kit.is_open || menuLoading}
                    data-testid={`kitchen-select-${kit.kitchen_id}`}
                  >
                    {/* Kitchen Image */}
                    {kit.image_url ? (
                      <img src={`${BACKEND_URL}${kit.image_url}`} alt={kit.kitchen_code} className="w-full h-36 object-cover" />
                    ) : (
                      <div className="w-full h-36 bg-gradient-to-br from-orange-50 to-amber-100 flex items-center justify-center">
                        <Store size={40} className="text-orange-300" />
                      </div>
                    )}
                    {/* Kitchen Info */}
                    <div className="p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-bold text-gray-900 text-sm">{kit.kitchen_code}</p>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${kit.is_open ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                          {kit.is_open ? "OPEN" : "CLOSED"}
                        </span>
                      </div>
                      <p className="text-[10px] text-gray-400 line-clamp-1">{kit.city}{kit.locality ? ` - ${kit.locality}` : ""}</p>
                      <div className="flex items-center gap-3 mt-1.5">
                        <span className="text-[10px] text-gray-500 flex items-center gap-1"><Navigation size={10} /> {kit.distance_km} km</span>
                        {kit.today_hours?.open && (
                          <span className="text-[10px] text-gray-500 flex items-center gap-1"><Clock size={10} /> {kit.today_hours.open} - {kit.today_hours.close}</span>
                        )}
                        {kit.menu_item_count > 0 && <span className="text-[10px] text-gray-500">{kit.menu_item_count} items</span>}
                      </div>
                      <div className="flex gap-1 mt-1.5">
                        {kit.serves_vegan && <span className="text-[9px] bg-green-50 text-green-700 px-1.5 py-0.5 rounded-full">Vegan</span>}
                        {kit.serves_vegetarian && <span className="text-[9px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-full">Veg</span>}
                        {kit.serves_non_vegetarian && <span className="text-[9px] bg-red-50 text-red-700 px-1.5 py-0.5 rounded-full">Non-Veg</span>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step: Menu */}
        {step === "menu" && (
          <div className="animate-fade-in">
            <div className="px-4 pt-4 pb-2">
              {/* Delivery address bar */}
              <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 mb-3">
                <div className="flex items-center gap-2 text-xs text-gray-600 min-w-0">
                  <MapPin size={13} className="text-green-600 flex-shrink-0" />
                  <span className="truncate">{selectedAddress?.address_line || "Delivery Address"}</span>
                </div>
                <button onClick={() => { setStep("address"); setSearchParams({}); sessionStorage.removeItem("sm_address_id"); }} className="text-xs text-green-600 hover:text-green-700 font-medium flex-shrink-0 ml-2" data-testid="switch-address-btn">
                  Switch
                </button>
              </div>
              <button onClick={() => { setStep("kitchen"); setSearchParams({ step: "kitchen", address: selectedAddress?.address_id }); }} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2" data-testid="back-to-kitchen-btn">
                <ArrowLeft size={16} /> Change Kitchen
              </button>
              <div className="flex items-center gap-2">
                <Store size={18} className="text-orange-600" />
                <h1 className="text-xl font-bold text-gray-900">{selectedKitchen?.kitchen_code || "Menu"}</h1>
                {selectedKitchen?.is_open && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700">OPEN</span>}
              </div>
              <p className="text-[10px] text-gray-400 mt-0.5">{selectedKitchen?.city}{selectedKitchen?.locality ? ` - ${selectedKitchen.locality}` : ""}</p>
            </div>

            {/* Search */}
            <div className="px-4 mt-3 mb-3">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search menu..." className="pl-9 bg-white" data-testid="menu-search" />
              </div>
            </div>

            {/* Category Filter Pills */}
            {allCategories.length > 0 && (
              <div className="px-4 mb-4 overflow-x-auto scrollbar-hide" data-testid="category-filter">
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedCategory("all")}
                    className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${selectedCategory === "all" ? "bg-green-600 text-white shadow-sm" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                    data-testid="category-pill-all"
                  >
                    All
                  </button>
                  {allCategories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap ${selectedCategory === cat ? "bg-green-600 text-white shadow-sm" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                      data-testid={`category-pill-${cat}`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {menuLoading ? (
              <div className="px-4 space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-64 bg-gray-100 rounded-xl animate-pulse" />)}</div>
            ) : menuItems.length === 0 ? (
              <div className="text-center py-16 px-4">
                <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600">No menu items available at this kitchen</p>
              </div>
            ) : (
              <div className="px-4">
                {categories.map((cat) => {
                  const catItems = filteredMenu.filter((i) => i.category === cat);
                  if (catItems.length === 0) return null;
                  return (
                    <div key={cat} className="mb-6">
                      <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3 border-b border-gray-100 pb-2">{cat}</h2>
                      <div className="space-y-4">
                        {catItems.map((item) => (
                          <MenuCard key={item.item_id} item={item} symbol={symbol} cartQty={getCartQuantityForItem(item.item_id)} onAdd={() => handleAddToCart(item)} />
                        ))}
                      </div>
                    </div>
                  );
                })}
                {filteredMenu.filter((i) => !i.category).length > 0 && (
                  <div className="mb-6">
                    <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3 border-b border-gray-100 pb-2">Other</h2>
                    <div className="space-y-4">
                      {filteredMenu.filter((i) => !i.category).map((item) => (
                        <MenuCard key={item.item_id} item={item} symbol={symbol} cartQty={getCartQuantityForItem(item.item_id)} onAdd={() => handleAddToCart(item)} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Customization Dialog */}
        <ItemCustomizationDialog
          item={customizeItem}
          open={!!customizeItem}
          onClose={() => setCustomizeItem(null)}
          onAddToCart={handleCustomizationAdd}
        />
      </div>
    </UserLayout>
  );
}

function MenuCard({ item, symbol, cartQty, onAdd }) {
  const imgSrc = item.image_path
    ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${item.image_path}`
    : item.image_url || "https://images.unsplash.com/photo-1606757819934-d61a9f7279d5?w=400";

  const isVeg = item.is_vegetarian;
  const isVegan = item.is_vegan;
  const nutrition = item.nutritional_info || {};
  const reviewCount = item.review_count || 0;
  const avgRating = item.avg_rating || 0;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid={`menu-item-${item.item_id}`}>
      {/* Horizontal image on top */}
      <div className="relative">
        <img src={imgSrc} alt={item.name} className="w-full h-44 object-cover" />
        {cartQty > 0 && (
          <div className="absolute top-2 right-2 bg-green-600 text-white text-xs font-bold px-2 py-1 rounded-full">{cartQty} in cart</div>
        )}
      </div>

      {/* Content */}
      <div className="p-3">
        {/* Veg/Vegan/Non-veg tag + Name */}
        <div className="flex items-center gap-2 mb-1">
          {isVegan ? (
            <span className="flex items-center gap-1 text-[10px] font-bold text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded">
              <span className="w-2.5 h-2.5 border border-green-600 rounded-sm flex items-center justify-center"><span className="w-1 h-1 bg-green-600 rounded-full" /></span>
              VEGAN
            </span>
          ) : isVeg ? (
            <span className="flex items-center gap-1 text-[10px] font-bold text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded">
              <span className="w-2.5 h-2.5 border border-green-600 rounded-sm flex items-center justify-center"><span className="w-1 h-1 bg-green-600 rounded-full" /></span>
              VEG
            </span>
          ) : (
            <span className="flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">
              <span className="w-2.5 h-2.5 border border-red-600 rounded-sm flex items-center justify-center"><span className="w-1 h-1 bg-red-600 rounded-full" /></span>
              NON-VEG
            </span>
          )}
          {item.is_bestseller && <span className="flex items-center gap-0.5 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded"><Star size={9} className="fill-amber-400 text-amber-400" /> BESTSELLER</span>}
          {item.is_gym_special && <span className="flex items-center gap-0.5 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded" data-testid={`gym-special-${item.item_id}`}><Dumbbell size={10} className="text-emerald-600" /> GYM SPECIAL</span>}
        </div>

        <h3 className="font-bold text-gray-900 text-base">{item.name}</h3>
        {item.description && <p className="text-[10px] text-gray-400 mt-0.5 text-justify leading-relaxed">{item.description}</p>}

        {/* Star Reviews */}
        <div className="flex items-center gap-1.5 mt-1.5" data-testid={`stars-${item.item_id}`}>
          <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star key={star} size={12}
                className={star <= Math.round(avgRating) ? "text-amber-400 fill-amber-400" : "text-gray-300"}
              />
            ))}
          </div>
          <span className="text-[9px] text-gray-400 font-medium">({reviewCount})</span>
        </div>

        {/* Superfood Angle */}
        {item.superfood_angle && (
          <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-xl p-2.5">
            <div className="flex items-start gap-2">
              <Dumbbell size={16} className="text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <p className="text-xs font-bold text-emerald-800 flex-1">{item.superfood_name || "Superfood Ingredient"}</p>
                  <span className="text-[8px] font-bold text-emerald-600 border border-emerald-400 bg-transparent px-1.5 py-0.5 rounded-full uppercase tracking-wider flex-shrink-0">Superfood</span>
                </div>
                <p className="text-[11px] text-emerald-600 leading-relaxed text-justify">{item.superfood_angle}</p>
              </div>
            </div>
          </div>
        )}

        {/* Nutritional content — kcal centered in pill, 3 small green circles below */}
        {(nutrition.calories || nutrition.protein || nutrition.carbs || nutrition.fat) && (
          <div className="mt-3" data-testid={`nutrition-gauges-${item.item_id}`}>
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-900/40 rounded-xl py-2 px-3 flex items-center justify-center gap-1.5 mb-2">
              <span className="text-xl font-extrabold leading-none text-green-700 dark:text-green-300">{Math.round(nutrition.calories || 0)}</span>
              <span className="text-[10px] font-semibold text-green-600 dark:text-green-400 mb-0.5">kcal Per Serving</span>
            </div>
            <div className="flex items-center justify-center">
              <div className="w-[90px] h-[90px] rounded-full flex flex-col items-center justify-center bg-white border-[3.5px] border-green-700">
                <span className="text-[10px] font-medium text-green-800 leading-none">Protein</span>
                <span className="text-base font-extrabold leading-tight mt-1 text-green-800">{Math.round(nutrition.protein || 0)}g</span>
              </div>
              <div className="w-[90px] h-[90px] rounded-full flex flex-col items-center justify-center bg-white border-[3.5px] border-green-500 -ml-5">
                <span className="text-[10px] font-medium text-green-700 leading-none">Carbs</span>
                <span className="text-base font-extrabold leading-tight mt-1 text-green-700">{Math.round(nutrition.carbs || 0)}g</span>
              </div>
              <div className="w-[90px] h-[90px] rounded-full flex flex-col items-center justify-center bg-white border-[3.5px] border-green-900 -ml-5">
                <span className="text-[10px] font-medium text-green-900 leading-none">Fat</span>
                <span className="text-base font-extrabold leading-tight mt-1 text-green-900">{Math.round(nutrition.fat || 0)}g</span>
              </div>
            </div>
          </div>
        )}

        {/* Price + Add */}
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
          <span className="text-lg font-extrabold text-gray-900">{symbol}{Math.round(item.base_price)}</span>
          <button onClick={onAdd} className="px-5 py-2 bg-green-50 text-green-700 font-bold text-sm rounded-xl border border-green-200 hover:bg-green-100 transition-colors" data-testid={`add-to-cart-${item.item_id}`}>
            ADD
          </button>
        </div>
      </div>
    </div>
  );
}

function NutritionGauge({ label, value, max, unit, color, bgColor }) {
  const percentage = Math.min((value / max) * 100, 100);
  const radius = 28;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="flex flex-col items-center" data-testid={`gauge-${label.toLowerCase()}`}>
      <div className="relative w-[66px] h-[66px]">
        <svg className="w-[66px] h-[66px] -rotate-90" viewBox="0 0 64 64">
          <circle cx="32" cy="32" r={radius} stroke={bgColor} strokeWidth="5" fill="none" />
          <circle cx="32" cy="32" r={radius} stroke={color} strokeWidth="5" fill="none"
            strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
            strokeLinecap="round" className="transition-all duration-500" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-[11px] font-extrabold text-gray-800 leading-none">{value}</span>
          <span className="text-[8px] text-gray-400 mt-0.5">{unit}</span>
        </div>
      </div>
      <span className="text-[9px] text-gray-500 font-semibold mt-1">{label}</span>
    </div>
  );
}
